/**
 * @deprecated Use `MobileAutopayDetailsModal` with mode="setup" instead.
 * This standalone screen is superseded by the unified modal-based autopay flow.
 */
import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, ScrollView, StyleSheet, TouchableOpacity, Alert, ActivityIndicator, KeyboardAvoidingView, Platform } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { StackScreenProps } from '@react-navigation/stack';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { useThemeColors, useThemedStyles, type ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { RNButton } from '@/components';
import { strings } from '@/constants/residents/strings';
import { useGetPaymentMethodsQuery, useSetupAutoPayMutation, useCancelAutoPayMutation, useGetAutoPayStatusByLeaseQuery, webPaymentsCompatApi, leasesApi } from '@/store/api';
import { useAppDispatch } from '@/store';
import { useToast } from '@/contexts/admin/ToastContext';
import { PaymentIcons } from '@/components/residents/payments/PaymentIcons';
import { MobileScreenHeader } from '@/components/residents/payments/MobileScreenHeader';
import { mobileScreenStyles } from '@/styles/mobileScreenStyles';
import { getPaymentMethodSourceId } from '@/utils/residents/paymentUtils';
import { useAmountInput } from '@/features/payments/hooks/useAmountInput';
import { CurrencyInput } from '@/components/residents/payments/shared/CurrencyInput';

type MobileAutopaySetupScreenProps = StackScreenProps<RootStackParamList, 'AutopaySetup'>;

export const MobileAutopaySetupScreen: React.FC = () => {
  const navigation = useNavigation<MobileAutopaySetupScreenProps['navigation']>();
  const route = useRoute<MobileAutopaySetupScreenProps['route']>();
  const { leaseId, edit } = route.params;
  
  const dispatch = useAppDispatch();
  const { showSuccess, showError, showWarning } = useToast();

  const { semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);

  const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
  const {
    amountType,
    customAmount,
    inputRef: amountInputRef,
    selectBalance,
    selectOther,
    handleAmountChange,
    setAmountType,
    setCustomAmount,
  } = useAmountInput();
  
  const { data: methodsData, isLoading: loadingMethods } = useGetPaymentMethodsQuery();
  const { data: autopayData, isLoading: loadingAutopay } = useGetAutoPayStatusByLeaseQuery(
    { leaseId },
    { skip: !edit || !leaseId },
  );
  const [setupAutopay, { isLoading: settingUp }] = useSetupAutoPayMutation();
  const [cancelAutopay, { isLoading: cancelling }] = useCancelAutoPayMutation();

  const paymentMethods = methodsData?.response || [];
  const defaultMethod = paymentMethods.find((m: any) => m.isDefault) || paymentMethods[0];

  const existingAutopay = useMemo(() => {
    const autopayList = Array.isArray(autopayData?.response) ? autopayData.response : [];
    return autopayList[0];
  }, [autopayData]);
  
  const isLoading = loadingMethods || loadingAutopay || settingUp || cancelling;

  // Set default values from existing autopay
  useEffect(() => {
    if (existingAutopay && edit) {
      setSelectedMethodId(existingAutopay.sourceId);
      const isCustom = existingAutopay.isCustomAmount || !existingAutopay.payFullAmount;
      setAmountType(isCustom ? 'other' : 'balance');
      if (isCustom && existingAutopay.amount) {
        setCustomAmount(String(existingAutopay.amount));
      }
    } else if (defaultMethod && !selectedMethodId) {
      // Use sourceId, not id - sourceId is what the backend expects
      const sourceId = Number(defaultMethod.sourceId) || Number(getPaymentMethodSourceId(defaultMethod));
      setSelectedMethodId(sourceId);
    }
  }, [existingAutopay, edit, defaultMethod, selectedMethodId]);

  const handleSetupAutopay = async () => {
    if (!selectedMethodId) {
      showWarning('Please select a payment method');
      return;
    }

    if (amountType === 'other') {
      const amount = parseFloat(customAmount);
      if (!amount || amount <= 0) {
        showWarning('Please enter a valid custom amount');
        return;
      }
    }

    try {
      const autopayPayload: any = {
        leaseId,
        sourceId: selectedMethodId,
        autopayIsLeaseLifetime: true,
      };

      if (amountType === 'balance') {
        autopayPayload.autopayIsFullAmount = true;
      } else {
        autopayPayload.autopayIsFullAmount = false;
        autopayPayload.autopayAmount = parseFloat(customAmount);
      }

      await setupAutopay(autopayPayload).unwrap();

      // Invalidate caches to ensure fresh data
      dispatch(webPaymentsCompatApi.util.invalidateTags(['autoPayStatus']));
      // Also reset lease cache since it includes recurringPayments
      dispatch(leasesApi.util.resetApiState());

      showSuccess('Autopay has been set up successfully!', {
        duration: 5000,
      });
      
      // Navigate back after a short delay
      setTimeout(() => {
        navigation.goBack();
      }, 500);
    } catch (err: any) {
      console.error('Failed to setup autopay:', err);
      const errorMessage = err?.data?.message || 'Failed to setup autopay. Please try again.';
      showError(errorMessage);
    }
  };

  const handleCancelAutopay = () => {
    Alert.alert(
      strings.mobilePayments.autopayCancelConfirmTitle,
      strings.mobilePayments.autopayCancelConfirmMessage,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Confirm',
          style: 'destructive',
          onPress: async () => {
            try {
              if (existingAutopay?.id) {
                await cancelAutopay(existingAutopay.id).unwrap();
                
                // Invalidate caches to ensure fresh data
                dispatch(webPaymentsCompatApi.util.invalidateTags(['autoPayStatus']));
                // Also reset lease cache since it includes recurringPayments
                dispatch(leasesApi.util.resetApiState());
                
                showSuccess('Autopay has been cancelled successfully', {
                  duration: 5000,
                });
                
                // Navigate back after a short delay
                setTimeout(() => {
                  navigation.goBack();
                }, 500);
              }
            } catch (err: any) {
              console.error('Failed to cancel autopay:', err);
              const errorMessage = err?.data?.message || 'Failed to cancel autopay. Please try again.';
              showError(errorMessage);
            }
          },
        },
      ]
    );
  };

  return (
    <KeyboardAvoidingView 
      style={{ flex: 1 }}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 0 : 20}
    >
      <View style={mobileScreenStyles.container}>
        <MobileScreenHeader 
          title={edit ? strings.mobilePayments.manageAutopay : strings.mobilePayments.setupAutopay} 
        />

        <ScrollView 
          style={mobileScreenStyles.content} 
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >

        {/* Amount Type Selection */}
        <View style={styles.section}>
          <Text style={mobileScreenStyles.sectionTitle}>{strings.mobilePayments.autopayAmountLabel}</Text>
          
          <TouchableOpacity
            style={[styles.optionCard, amountType === 'balance' && styles.optionCardActive]}
            onPress={selectBalance}
          >
            <View style={styles.optionContent}>
              <View style={styles.optionLeft}>
                <Text style={styles.optionTitle}>{strings.mobilePayments.autopayFullBalance}</Text>
                <Text style={styles.optionDescription}>Pay the full balance due each month</Text>
              </View>
              {amountType === 'balance' && (
                <View style={styles.optionCheck}>
                  <PaymentIcons.Check />
                </View>
              )}
            </View>
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.optionCard, amountType === 'other' && styles.optionCardActive]}
            onPress={selectOther}
          >
            <View style={styles.optionContent}>
              <View style={styles.optionLeft}>
                <Text style={styles.optionTitle}>{strings.mobilePayments.autopayFixedAmount}</Text>
                <Text style={styles.optionDescription}>Pay a fixed amount each month</Text>
              </View>
              {amountType === 'other' && (
                <View style={styles.optionCheck}>
                  <PaymentIcons.Check />
                </View>
              )}
            </View>
          </TouchableOpacity>

          {/* Custom Amount Input */}
          {amountType === 'other' && (
            <View style={styles.customAmountInput}>
              <Text style={styles.inputLabel}>Monthly Amount</Text>
              <CurrencyInput
                ref={amountInputRef}
                containerStyle={styles.amountInputContainer}
                currencySymbolStyle={styles.currencySymbol}
                inputStyle={styles.amountInput}
                value={customAmount}
                onChangeText={handleAmountChange}
                placeholder="0.00"
                placeholderTextColor={semantic.text.tertiary}
              />
            </View>
          )}
        </View>

        {/* Payment Method Selection */}
        <View style={styles.section}>
          <Text style={mobileScreenStyles.sectionTitle}>{strings.mobilePayments.autopayPaymentMethod}</Text>
          
          {loadingMethods ? (
            <ActivityIndicator size="large" color={semantic.interactive.primary} />
          ) : (
            <View>
              {paymentMethods.map((method: any) => {
                const methodSourceId = Number(method.sourceId) || method.id;
                return (
                  <TouchableOpacity
                    key={method.id}
                    style={[styles.methodCard, selectedMethodId === methodSourceId && styles.methodCardActive]}
                    onPress={() => setSelectedMethodId(methodSourceId)}
                  >
                    <View style={styles.methodContent}>
                      <View style={styles.methodLeft}>
                        <View style={styles.methodIcon}>
                          {method.type === 'card' ? <PaymentIcons.Card /> : <PaymentIcons.Bank />}
                        </View>
                        <View>
                          <Text style={styles.methodName}>{method.name || 'Payment Method'}</Text>
                          <Text style={styles.methodDetails}>
                            {method.type === 'card' 
                              ? `${strings.mobilePayments.cardEndingIn} ${method.last4}`
                              : `${strings.mobilePayments.bankAccountEndingIn} ${method.last4}`
                            }
                          </Text>
                        </View>
                      </View>
                      {selectedMethodId === methodSourceId && (
                        <View style={styles.methodCheck}>
                          <PaymentIcons.Check />
                        </View>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          )}
        </View>

        {/* Actions */}
        <View style={styles.actions}>
          {isLoading ? (
            <ActivityIndicator size="large" color={semantic.interactive.primary} />
          ) : (
            <>
              <RNButton
                title={edit ? 'Update Autopay' : strings.mobilePayments.setupAutopay}
                onPress={handleSetupAutopay}
                variant="tertiary"
                style={styles.setupButton}
                textStyle={styles.setupButtonText}
                disabled={!selectedMethodId}
              />
              
              {edit && existingAutopay && (
                <RNButton
                  title="Cancel Autopay"
                  onPress={handleCancelAutopay}
                  variant="outlined"
                  style={styles.cancelButton}
                />
              )}
            </>
          )}
        </View>
      </ScrollView>
    </View>
    </KeyboardAvoidingView>
  );
};

const makeStyles = ({ semantic, components }: ThemeContextValue) => StyleSheet.create({
  section: {
    marginBottom: 24,
    paddingHorizontal: 16,
  },
  optionCard: {
    backgroundColor: components.card.background,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: 'transparent',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  optionCardActive: {
    borderColor: semantic.interactive.primary,
  },
  optionContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  optionLeft: {
    flex: 1,
  },
  optionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: semantic.text.primary,
    marginBottom: 4,
  },
  optionDescription: {
    fontSize: 13,
    color: semantic.text.tertiary,
  },
  optionCheck: {
    marginLeft: 12,
  },
  customAmountInput: {
    marginTop: 12,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: semantic.text.primary,
    marginBottom: 8,
  },
  amountInputContainer: {
    backgroundColor: components.card.background,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: semantic.border.secondary,
  },
  currencySymbol: {
    fontSize: 20,
    fontWeight: '700',
    color: semantic.text.primary,
    marginRight: 8,
  },
  amountInput: {
    fontSize: 20,
    fontWeight: '600',
    color: semantic.text.primary,
  },
  methodCard: {
    backgroundColor: components.card.background,
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: 'transparent',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.06,
    shadowRadius: 2,
    elevation: 2,
  },
  methodCardActive: {
    borderColor: semantic.interactive.primary,
  },
  methodContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  methodLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  methodIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: components.avatar.background,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 12,
  },
  methodName: {
    fontSize: 16,
    fontWeight: '600',
    color: semantic.text.primary,
    marginBottom: 2,
  },
  methodDetails: {
    fontSize: 13,
    color: semantic.text.tertiary,
  },
  methodCheck: {
    marginLeft: 12,
  },
  actions: {
    paddingHorizontal: 16,
    marginBottom: 32,
  },
  setupButton: {
    backgroundColor: semantic.interactive.primary,
    paddingVertical: 14,
    marginBottom: 12,
  },
  setupButtonText: {
    // White text on the blue brand fill (interactive.primary) — stays white in both themes.
    color: '#FFFFFF',
    fontWeight: '600',
    fontSize: 16,
  },
  cancelButton: {
    borderColor: semantic.status.error,
    borderWidth: 2,
  },
});

